import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import API_ENDPOINTS from '../../config/api';
import { playSfx, setBackground, setVideoAudio, setAmbient, onBlocked, retryBlocked } from '../../audio/audioEngine';
import { AUDIO_HOST_DEFAULT, stemForScreen } from '../../audio/sfxManifest';

const UnlockButton = styled.button`
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 50;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid #f59e0b;
  background: rgba(245, 158, 11, 0.15);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
`;

/**
 * AudioPlayer — the audio half of a screen. Renders nothing in normal
 * operation; audio is a screen command (specs/audio-management.md).
 *
 * Two channels over the instance socket room:
 *   'audio-play'        one-shot SFX  { key, volume, host }
 *   'flow-audio-state'  background    { background: {key, volume}|null, host }
 *
 * Only the audio-host screen plays (default A1) — venue speakers hang off one
 * machine. Virtual screens (play-view previews) stay silent unless the URL
 * carries audio=1, so a facilitator's play view never doubles the room audio.
 *
 * If the browser blocks autoplay (screen rebooted straight into /screen/A1,
 * no gesture yet), a "Salli äänet" button appears; one click resumes the
 * remembered background.
 */
const AudioPlayer = ({ socket, instanceId, screenCode, audioEnabled, clockOffset = 0 }) => {
  const [needsUnlock, setNeedsUnlock] = useState(false);
  // SFX arrive on their own event, so the mute flag has to outlive the
  // background handler that set it.
  const mutedRef = useRef(false);

  useEffect(() => {
    if (!socket || !audioEnabled) return undefined;
    onBlocked(() => setNeedsUnlock(true));

    const isMe = (host) => (host || AUDIO_HOST_DEFAULT) === screenCode;
    const handleSfx = (data) => {
      if (mutedRef.current) return;
      if (isMe(data?.host)) playSfx(data.key, data.volume ?? 1);
    };
    const handleBackground = (data) => {
      // Master mute: the facilitator has silenced the room. Covers every
      // channel, including the video soundtrack — the walls' own <video>
      // elements are already muted, so this is all the sound Kuutio makes.
      mutedRef.current = !!data?.muted;
      if (data?.muted) {
        setAmbient(null, { fadeMs: 200 });
        setBackground(null, { fadeMs: 200 });
        setVideoAudio(null);
        return;
      }

      // Ambient soundscape plays on EVERY screen — each wall renders its own
      // stem of the premade layout (deterministic from scape + seed + code)
      const amb = data?.ambient;
      if (amb?.scape) {
        setAmbient(stemForScreen(amb.scape, amb.seed, screenCode), { volume: amb.volume ?? 0.5 });
      } else {
        setAmbient(null);
      }

      // Music + video soundtrack stay on the audio-host screen only
      if (!isMe(data?.host)) { setBackground(null); setVideoAudio(null); return; }
      setBackground(data?.background || null);
      setVideoAudio(data?.videoAudio || null, { clockOffset });
    };

    socket.on('audio-play', handleSfx);
    socket.on('flow-audio-state', handleBackground);

    // Late joiner / reboot: pick up the background that is already playing
    fetch(API_ENDPOINTS.FLOW_STATE(instanceId))
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (data?.audioState) handleBackground(data.audioState); })
      .catch(() => {});

    return () => {
      socket.off('audio-play', handleSfx);
      socket.off('flow-audio-state', handleBackground);
      setBackground(null, { fadeMs: 200 });
      setVideoAudio(null);
      setAmbient(null, { fadeMs: 200 });
    };
  }, [socket, instanceId, screenCode, audioEnabled, clockOffset]);

  if (!needsUnlock || !audioEnabled) return null;
  return (
    <UnlockButton onClick={() => { setNeedsUnlock(false); retryBlocked(); }}>
      🔊 Salli äänet
    </UnlockButton>
  );
};

export default AudioPlayer;
